
import { addDays } from "date-fns";
import { Product, UsageRecord, StockUpdate } from "@/lib/types";
import { getISODate, formatDateForChart } from "./dateUtils";

/**
 * Prepare data for usage over time chart for a specific product
 */
export const prepareProductUsageData = (
  selectedProductId: string | undefined, 
  startDate: Date, 
  filteredUsageRecords: UsageRecord[]
) => {
  if (!selectedProductId) return [];
  
  const dateMap: Record<string, { date: string; usage: number }> = {};
  
  // Create a map of dates
  let currentDate = new Date(startDate);
  const today = new Date();
  
  while (currentDate <= today) {
    const dateStr = getISODate(currentDate);
    dateMap[dateStr] = { date: formatDateForChart(currentDate), usage: 0 };
    currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
  }
  
  // Add usage data for selected product
  filteredUsageRecords
    .filter(record => record.productId === selectedProductId)
    .forEach(record => {
      const recordDate = new Date(record.date);
      const dateStr = getISODate(recordDate);
      
      if (dateMap[dateStr]) {
        dateMap[dateStr].usage += record.quantity;
      }
    });
  
  return Object.values(dateMap);
};

/**
 * Prepare data for stock level history
 */
export const prepareStockLevelHistory = (
  selectedProduct: Product | undefined, 
  startDate: Date, 
  usageRecords: UsageRecord[], 
  stockUpdates: StockUpdate[]
) => {
  if (!selectedProduct) return [];
  
  // Create a timeline of stock changes
  const stockTimeline: { date: Date; stock: number }[] = [];
  
  // Start with current stock and work backwards
  let currentStock = selectedProduct.currentStock;
  
  // Get all usage and stock updates for this product
  const productUsage = usageRecords
    .filter(record => record.productId === selectedProduct.id)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  
  const productStockUpdates = stockUpdates
    .filter(update => update.productId === selectedProduct.id)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  
  // Add current stock point
  stockTimeline.push({ 
    date: new Date(), 
    stock: currentStock 
  });
  
  // Combine and sort all events by date (newest first)
  const allEvents = [
    ...productUsage.map(record => ({ 
      date: new Date(record.date), 
      change: -record.quantity, 
      type: 'usage' as const
    })),
    ...productStockUpdates.map(update => ({ 
      date: new Date(update.date), 
      change: update.quantity, 
      type: 'stock' as const
    }))
  ].sort((a, b) => b.date.getTime() - a.date.getTime());
  
  // Reconstruct the stock timeline
  for (const event of allEvents) {
    if (event.date < startDate) continue;
    
    const previousStock = currentStock;
    // Reverse the change to go back in time
    currentStock = currentStock - event.change;
    
    stockTimeline.push({
      date: event.date,
      stock: previousStock
    });
  }
  
  // Sort by date (oldest first) for charting
  stockTimeline.sort((a, b) => a.date.getTime() - b.date.getTime());
  
  // Format for chart display
  return stockTimeline.map(point => ({
    date: formatDateForChart(point.date),
    stock: point.stock
  }));
};

/**
 * Calculate product-specific statistics
 */
export const calculateProductStats = (
  selectedProduct: Product | undefined, 
  filteredUsageRecords: UsageRecord[], 
  filteredStockUpdates: StockUpdate[]
) => {
  if (!selectedProduct) {
    return { usage: 0, added: 0, daysRemaining: 0, usageRate: 0, restockDate: new Date() };
  }
  
  const productUsage = filteredUsageRecords
    .filter(record => record.productId === selectedProduct.id)
    .reduce((sum, record) => sum + record.quantity, 0);
  
  const productStockAdded = filteredStockUpdates
    .filter(update => update.productId === selectedProduct.id)
    .reduce((sum, update) => sum + update.quantity, 0);
  
  // Calculate usage rate based on data
  const usageRate = selectedProduct.actualUsageRate || selectedProduct.usageRate;
  
  // Days remaining calculation
  const daysRemaining = Math.floor(selectedProduct.currentStock / usageRate);
  
  // Predict restock date
  const restockDate = addDays(new Date(), daysRemaining);
  
  return {
    usage: productUsage,
    added: productStockAdded,
    daysRemaining,
    usageRate,
    restockDate
  };
};

/**
 * Calculate total statistics (for all products)
 */
export const calculateTotalStats = (
  products: Product[],
  filteredUsageRecords: UsageRecord[],
  filteredStockUpdates: StockUpdate[]
) => {
  const totalUsage = filteredUsageRecords.reduce(
    (sum, record) => sum + record.quantity, 
    0
  );
  
  const totalStockAdded = filteredStockUpdates.reduce(
    (sum, update) => sum + update.quantity, 
    0
  );
  
  const lowStockProducts = products.filter(product => 
    Math.floor(product.currentStock / (product.actualUsageRate || product.usageRate)) < product.minStockAlert
  );

  return {
    totalUsage,
    totalStockAdded,
    lowStockProducts
  };
};
